import { useEffect, useMemo } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { CircleLoadingAnimation } from '@vgw/multibrand-fe-circle-loading-animation'
import { useToast } from '@vgw/multibrand-fe-toast'
import { EditLimit } from '@/features/purchase-limits/edit-limit'
import { RemoveLimit } from '@/features/purchase-limits/remove-limit'
import { usePurchaseLimitsQuery } from '@/features/purchase-limits/use-purchase-limits-query/use-purchase-limits-query'
import {
  findRsgLimitForType,
  isLastActiveLimit,
  mergeUserAndRsgLimits,
} from '@/features/purchase-limits/purchase-limits-utils'
import { PlayerDefinedPurchaseLimit } from '@/features/purchase-limits/purchase-limits'
import { createFileRoute } from '@tanstack/react-router'
import { TertiaryPageLayout } from '@/routes/-tertiary-page-layout'

export const Route = createFileRoute(
  '/_app/your-play/gameplay-limits/manage/$type',
)({
  component: ManagePurchaseLimits,
})

function ManagePurchaseLimits() {
  const { t } = useLingui()
  const { show } = useToast()
  const { type = '' } = Route.useParams()
  const { limits, isLoading, error } = usePurchaseLimitsQuery()

  useEffect(() => {
    if (error) {
      show({
        description: t`Something went wrong.`,
        variant: 'warning',
        close: false,
      })
    }
  }, [error, show, t])

  const currentLimit = useMemo(
    () =>
      limits?.find(
        (limit) => limit.type === type,
      ) as PlayerDefinedPurchaseLimit,
    [limits, type],
  )

  const rsgLimit = useMemo(() => {
    if (!currentLimit) return

    return findRsgLimitForType(limits, currentLimit.type)
  }, [limits, currentLimit])

  const canShowLimits = !isLoading && !error && currentLimit

  const isLastLimit = useMemo(() => {
    if (!limits || limits.length === 0) return false

    return isLastActiveLimit(currentLimit, mergeUserAndRsgLimits(limits))
  }, [currentLimit, limits])

  return (
    <TertiaryPageLayout headerText={t`Manage Purchase Limits`}>
      <div className="flex flex-col gap-3 p-2">
        <p className="text-center font-family-body leading-6 font-light text-content">
          <Trans>
            You can increase, decrease or remove your set purchase limit
          </Trans>
        </p>

        {!!isLoading && <CircleLoadingAnimation className="mt-[60px] w-full" />}

        {!!canShowLimits && (
          <>
            <EditLimit
              type={currentLimit.type}
              currentLimit={currentLimit.value}
              resetsOn={currentLimit?.resetDate}
              scheduledIncrease={currentLimit?.scheduledIncrease}
              scheduledRemoval={currentLimit?.scheduledRemoval}
              rsgLimit={rsgLimit?.value}
            />
            <RemoveLimit
              type={currentLimit.type}
              currentLimit={currentLimit.value}
              resetsOn={currentLimit?.resetDate}
              scheduledIncrease={currentLimit?.scheduledIncrease}
              scheduledRemoval={currentLimit?.scheduledRemoval}
              isLastLimit={isLastLimit}
            />
          </>
        )}
      </div>
    </TertiaryPageLayout>
  )
}
